import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import PopupState, { bindTrigger, bindMenu } from "material-ui-popup-state";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { getContentTypes } from "../actions/contentActions";
import DropDownMenu from "../themes/overrides/DropDownMenu";
import CreateEntry from "../pages/entry/CreateEntry";
import { getEntryForDrawer } from "../actions/entryActions";
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

export default function RightDrawer({ contentTypeId, setParentSelectedRef, fieldId, editEntry = null, isEditMode = false, onEditClose }) {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [selectedContentType, setSelectedContentType] = useState(null);
  const contentTypes = useSelector((state) => state.form.contentTypes);
  const drawerEntrySelector = useSelector((state) => state.entry.drawerEntry)
  const workspaceId = localStorage.getItem('workspaceID')
  const [isDrawerReady, setIsDrawerReady] = useState(false);
  const [drawerEntry, setDrawerEntry] = useState(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!contentTypes || contentTypes.length === 0) {
      dispatch(getContentTypes(workspaceId));
    }
  }, [dispatch, contentTypes, workspaceId]);

  const parseEntryId = (entryPath) => {
    if (typeof entryPath === 'string') {
      return entryPath.split('/').pop();
    }
    return entryPath;
  };

  useEffect(() => {
    const initializeEditMode = async () => {
      if (isEditMode && editEntry) {
        setIsDrawerReady(false);
        const entryId = parseEntryId(editEntry._id);
        await dispatch(getEntryForDrawer(workspaceId, entryId));
        setHasInitialized(true)
      }
    };

    if (isEditMode && editEntry) {
      initializeEditMode();
    }
  }, [isEditMode, editEntry, workspaceId, dispatch]);

  useEffect(() => {
    if (isEditMode && editEntry && contentTypes.length > 0 && hasInitialized && drawerEntrySelector && !open) {
      setDrawerEntry(drawerEntrySelector);

      const currentParams = new URLSearchParams(location.search);
      currentParams.set("contentTypeId", drawerEntrySelector.entry?.contentTypeId);

      navigate({
        pathname: location.pathname,
        search: `?${currentParams.toString()}`,
      });

      const matchedContentType = contentTypes.find(ct => ct._id === drawerEntrySelector.entry?.contentTypeId);
      if (matchedContentType && !isDrawerReady) {
        setSelectedContentType({ contentType: matchedContentType });
        setIsDrawerReady(true);
        setOpen(true);
      }
    }
  }, [isEditMode, editEntry, drawerEntrySelector, hasInitialized, isDrawerReady, open, contentTypes, location.search, navigate]);

  const handleClose = async (newEntry) => {
    // Only update refs if there's a new entry
    if (newEntry?._id && setParentSelectedRef) {
      const entryToAdd = {
        _id: newEntry._id,
        identifier: newEntry.identifier,
        published: newEntry.published,
        contentTypeId: newEntry.contentTypeId || contentTypeId,
        content_type_name: newEntry.content_type_name,
        data: newEntry.data || {},
        localeData: newEntry.localeData || {},
        references: newEntry.references || {}
      };

      setParentSelectedRef(prev => {
        const prevEntries = prev[fieldId] || [];
        const existingEntryIndex = prevEntries.findIndex(e => e._id === entryToAdd._id);

        if (existingEntryIndex >= 0) {
          const updatedEntries = [...prevEntries];
          updatedEntries[existingEntryIndex] = entryToAdd;
          return {
            ...prev,
            [fieldId]: updatedEntries
          };
        } else {
          return {
            ...prev,
            [fieldId]: [...prevEntries, entryToAdd]
          };
        }
      });
    }

    //setSelectedContentType(null);
    //setOpen(false);
    //setIsDrawerReady(false);
    handleCloseDrawer();
  };

  const handleCloseDrawer = () => {
    setOpen(false);
    setSelectedContentType(null);
    setIsDrawerReady(false);
    setHasInitialized(false);
    setDrawerEntry(null);

    if (onEditClose) {
      onEditClose();
    }

    const currentParams = new URLSearchParams(location.search);
    currentParams.delete("contentTypeId");
    currentParams.delete("previousContentTypeId");

    navigate({
      pathname: location.pathname,
      search: currentParams.toString() ? `?${currentParams.toString()}` : '',
    }, { replace: true });
  };


  const handleMenuItemClick = (contentType) => {
    setSelectedContentType(null);

    const currentParams = new URLSearchParams(location.search);
    currentParams.delete("contentTypeId");
    currentParams.delete("previousContentTypeId");

    currentParams.set("contentTypeId", contentType._id);

    navigate({
      pathname: location.pathname,
      search: `?${currentParams.toString()}`,
    }, { replace: true });

    if (isEditMode) {
      setParentSelectedRef(prev => ({
        ...prev,
        editingRefId: null
      }));
    }

    setSelectedContentType({ contentType });
    setIsDrawerReady(true);
    setOpen(true);
  };

  const [formattedInitialData, setFormattedInitialData] = useState(null);

  useEffect(() => {
    if (editEntry && !formattedInitialData) {
      const formatted = {
        identifier: editEntry.identifier,
        ...editEntry.data,
        ...Object.entries(editEntry.localeData || {}).reduce((acc, [locale, data]) => {
          Object.entries(data).forEach(([key, value]) => {
            acc[key] = acc[key] || {};
            acc[key][locale] = value;
          });
          return acc;
        }, {})
      };
      setFormattedInitialData(formatted);
    }
  }, [editEntry, formattedInitialData]);



  // const handleEditItemClick = () => {
  //   setSelectedContentType(null);
  //   console.log('Edit Entry : ', editEntry);
  //   const currentParams = new URLSearchParams(location.search);
  //   currentParams.delete("contentTypeId");
  //   currentParams.delete("previousContentTypeId");

  //   currentParams.set("contentTypeId", editEntry._id);

  //   navigate({
  //     pathname: location.pathname,
  //     search: `?${currentParams.toString()}`,
  //   }, { replace: true });

  //   setSelectedContentType({ editEntry });
  //   setOpen(true);
  // };


  return (
    <div>
      <Box>
        <PopupState variant="popover" popupId="demo-popup-menu">
          {(popupState) => (
            <>
              <Button
                variant="outlined"
                {...bindTrigger(popupState)}
                color="primary"
                sx={{ width: isMobile ? '100%' : '220px' }}
              >
                + Add New Reference
              </Button>

              <DropDownMenu {...bindMenu(popupState)}>
                {contentTypes.map((contentType) => (
                  <MenuItem
                    key={contentType._id}
                    onClick={() => {
                      popupState.close();
                      handleMenuItemClick(contentType);
                    }}
                  >
                    {contentType.name}
                  </MenuItem>
                ))}
              </DropDownMenu>
            </>
          )}
        </PopupState>
      </Box>

      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
      >
        <Box sx={{ width: "80vw", p: 2 }} role="presentation">
          {isDrawerReady && (
            <CreateEntry
              contentType={selectedContentType}
              onConfirm={handleClose}
              isDrawer={true}
              fieldId={fieldId}
              editEntry={drawerEntry}
              isDrawerEdit={isEditMode}
              initialFormData={formattedInitialData}
            />
          )}
        </Box>
      </Drawer>
    </div>
  );
}
